import React, { useMemo, useState } from 'react';
import { Alert, Button, Checkbox, FormControlLabel, Grid, Stack, TextField } from '@mui/material';
import { getClient, lookupCompany } from '../../../../services/SalesService';

const normalizeRut = (value) => (value || '').replace(/\D/g, '');

const isSimpleSequence = (value) => (
  '01234567890123456789'.includes(value) ||
  '98765432109876543210'.includes(value)
);

const isValidRut = (value) => {
  const rut = normalizeRut(value);
  if (rut.length !== 12) return false;
  if (rut.split('').every((digit) => digit === rut[0])) return false;
  if (isSimpleSequence(rut)) return false;

  const weights = [4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const sum = rut.slice(0, 11).split('').reduce(
    (acc, digit, index) => acc + Number(digit) * weights[index],
    0
  );
  const candidate = 11 - (sum % 11);
  const expected = candidate > 9 ? 0 : candidate;
  return expected === Number(rut[11]);
};

const StepClient = ({
  clientInfo, setClientInfo,
  companyInfo, setCompanyInfo,
  errors, setErrors,
  skipClient, setSkipClient,
  needsCompany, setNeedsCompany,
  adenda, onAdendaChange,
  locationId,
}) => {
  const [companyLookupLoading, setCompanyLookupLoading] = useState(false);
  const normalizedCompanyRut = useMemo(() => normalizeRut(companyInfo.ci), [companyInfo.ci]);
  const companyRutValid = useMemo(() => isValidRut(companyInfo.ci), [companyInfo.ci]);
  const capitalizeFirstLetter = (str) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const handleFieldChange = (field, value) => {
    // Capitalizar primera letra para nombre y apellido
    let processedValue = value;
    if (field === 'name' || field === 'lastName') {
      // Si el campo está vacío o el usuario está escribiendo desde el principio
      if (value.length === 0) {
        processedValue = value;
      } else if (value.length === 1) {
        // Primera letra siempre en mayúscula
        processedValue = value.toUpperCase();
      } else {
        // Primera letra en mayúscula, resto mantiene lo que escriba el usuario
        processedValue = value.charAt(0).toUpperCase() + value.slice(1);
      }
    }
    setClientInfo(prev => ({ ...prev, [field]: processedValue }));
    setErrors(prev => ({ ...prev, [field]: '' }));
    setSkipClient(false);
  };

  const handleCompanyFieldChange = (field, value) => {
    if (field === 'ci') {
      setCompanyInfo(prev => ({
        ...prev,
        ci: normalizeRut(value),
        name: '',
        fiscalAddress: '',
        lookupStatus: 'idle',
        lookupSource: null,
        manualRegistrationConfirmed: false,
      }));
    } else {
      setCompanyInfo(prev => ({ ...prev, [field]: value }));
    }
    setErrors(prev => ({ ...prev, [`company_${field}`]: '' }));
  };

  const handleClientBlur = async () => {
    if (clientInfo.ci) {
      try {
        const client = await getClient(clientInfo.ci);
        if (client) {
          setClientInfo(prev => ({
            ...prev,
            name: client.firstName ? capitalizeFirstLetter(client.firstName) : '',
            lastName: client.lastName ? capitalizeFirstLetter(client.lastName) : '',
            email: client.email || '',
            phone: client.phone || '',
          }));
        }
      } catch (error) {
        console.log('Cliente no encontrado o error al buscar cliente.', error);
      }
    }
  };

  const handleCompanyLookup = async () => {
    if (!companyRutValid || companyLookupLoading) return;
    setCompanyLookupLoading(true);
    setErrors(prev => ({ ...prev, company_ci: '' }));
    try {
      const result = await lookupCompany(normalizedCompanyRut, locationId);
      if (result?.found) {
        setCompanyInfo(prev => ({
          ...prev,
          ci: result.rut || normalizedCompanyRut,
          name: result.name || '',
          fiscalAddress: result.fiscalAddress || '',
          lookupStatus: 'found',
          lookupSource: result.source || 'LOCAL',
          manualRegistrationConfirmed: false,
        }));
      } else {
        setCompanyInfo(prev => ({
          ...prev,
          ci: result?.rut || normalizedCompanyRut,
          name: '',
          fiscalAddress: '',
          lookupStatus: 'not_found',
          lookupSource: result?.source || 'NONE',
          manualRegistrationConfirmed: false,
        }));
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, company_ci: 'No se pudo buscar el RUT' }));
    } finally {
      setCompanyLookupLoading(false);
    }
  };

  const handleCompanyRutBlur = () => {
    if (companyRutValid && companyInfo.lookupStatus === 'idle') {
      handleCompanyLookup();
    }
  };

  const handleManualCompanyRegistration = () => {
    setCompanyInfo(prev => ({
      ...prev,
      lookupStatus: 'manual',
      manualRegistrationConfirmed: true,
    }));
    setErrors(prev => ({ ...prev, company_name: '', company_fiscalAddress: '' }));
  };

  return (
    <Grid container spacing={2}>
      {[['ci', 'Cédula'], ['name', 'Nombre'], ['lastName', 'Apellido'], ['email', 'Correo'], ['phone', 'Teléfono']].map(([field, label]) => (
        <Grid item xs={field === 'email' || field === 'phone' ? 6 : 4} key={field}>
          <TextField
            label={label}
            value={clientInfo[field]}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            onBlur={field === 'ci' ? handleClientBlur : undefined}
            fullWidth
            error={!!errors[field]}
            helperText={errors[field]}
          />
        </Grid>
      ))}

      <Grid item xs={12}>
        <FormControlLabel
          control={<Checkbox checked={needsCompany} onChange={(e) => setNeedsCompany(e.target.checked)} />}
          label="Necesito factura con RUT"
        />
      </Grid>

      {needsCompany && (
        <>
          <Grid item xs={12}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
              <TextField
                label="RUT"
                value={companyInfo.ci}
                onChange={(e) => handleCompanyFieldChange('ci', e.target.value)}
                onBlur={handleCompanyRutBlur}
                fullWidth
                error={!!errors.company_ci || (!!companyInfo.ci && !companyRutValid)}
                helperText={errors.company_ci || (companyInfo.ci && !companyRutValid ? 'Ingresá un RUT válido' : ' ')}
              />
              <Button
                variant="contained"
                onClick={handleCompanyLookup}
                disabled={!companyRutValid || companyLookupLoading}
                sx={{ minWidth: 120, height: '56px !important', minHeight: '56px !important' }}
              >
                {companyLookupLoading ? 'Buscando...' : 'Buscar'}
              </Button>
            </Stack>
          </Grid>

          {companyInfo.lookupStatus === 'found' && (
            <Grid item xs={12}>
              <Alert severity="success">Datos fiscales encontrados</Alert>
            </Grid>
          )}

          {companyInfo.lookupStatus === 'not_found' && (
            <Grid item xs={12}>
              <Stack spacing={1}>
                <Alert severity="warning">
                  No se encontró el RUT. Revisá el número antes de registrarlo manualmente.
                </Alert>
                <Button variant="outlined" onClick={handleManualCompanyRegistration}>
                  No se encontró RUT, registrar de todas formas
                </Button>
              </Stack>
            </Grid>
          )}

          {(companyInfo.lookupStatus === 'found' || companyInfo.manualRegistrationConfirmed) && (
            <>
              <Grid item xs={6}>
                <TextField
                  label="Razón social"
                  value={companyInfo.name}
                  onChange={(e) => handleCompanyFieldChange('name', e.target.value)}
                  fullWidth
                  disabled={companyInfo.lookupStatus === 'found'}
                  error={!!errors.company_name}
                  helperText={errors.company_name}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Dirección fiscal"
                  value={companyInfo.fiscalAddress}
                  onChange={(e) => handleCompanyFieldChange('fiscalAddress', e.target.value)}
                  fullWidth
                  disabled={companyInfo.lookupStatus === 'found'}
                  error={!!errors.company_fiscalAddress}
                  helperText={errors.company_fiscalAddress}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Adenda"
                  value={adenda}
                  onChange={(e) => onAdendaChange(e.target.value)}
                  helperText="Este campo es opcional"
                  multiline
                  minRows={2}
                  fullWidth
                />
              </Grid>
            </>
          )}
        </>
      )}
    </Grid>
  );
};

export default StepClient;
